/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import moment from 'moment-timezone';
import { lodash } from '@ba-ui-toolkit/utils';
import { DatePicker as CarbonDatePicker, DatePickerInput } from 'carbon';

const { uniqueId } = lodash;

export const datePropType = PropTypes.oneOfType([
	PropTypes.string,
	PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])),
	PropTypes.object,
	PropTypes.number,
]);

export const localPropType = PropTypes.oneOf([
	'ar',
	'at',
	'be',
	'bg',
	'bn',
	'cat',
	'cs',
	'cy',
	'da',
	'de',
	'en',
	'en',
	'eo',
	'es',
	'et',
	'fa',
	'fi',
	'fr',
	'gr',
	'he',
	'hi',
	'hr',
	'hu',
	'id',
	'it',
	'ja',
	'ko',
	'lt',
	'lv',
	'mk',
	'mn',
	'ms',
	'my',
	'nl',
	'no',
	'pa',
	'pl',
	'pt',
	'ro',
	'ru',
	'si',
	'sk',
	'sl',
	'sq',
	'sr',
	'sv',
	'th',
	'tr',
	'uk',
	'vn',
	'zh',
]);

export default class DatePicker extends Component {
	static propTypes = {
		/** The value of the date value provided */
		date: datePropType,
		/** The value of the start date value provided. It could be a date, a date number,
		 * a date string, an array of dates.
		 */
		startDate: datePropType,
		/** The value of the end date value provided. It could be a date, a date number,
		 * a date string, an array of dates.
		 */
		endDate: datePropType,
		/** The CSS class names. */
		className: PropTypes.string,
		/** Placeholder text when no date is selected */
		placeholder: PropTypes.string,
		/** Placeholder text when no start date is selected */
		startDatePlaceholderText: PropTypes.string,
		/** Placeholder text when no start date is selected */
		endDatePlaceholderText: PropTypes.string,
		/** The date format. */
		dateFormat: PropTypes.string,
		/** The language locale used to format the days of the week, months, and numbers. */
		locale: localPropType,
		/** The type of the date picker */
		datePickerType: PropTypes.oneOf(['simple', 'single', 'range']),
		/** To use the short version with no calendar pciker */
		short: PropTypes.bool,
		/** The `change` event handler. */
		onChange: PropTypes.func,
		/** The `close` event handler. */
		onClose: PropTypes.func,
		/** Specify whether the control is disabled */
		disabled: PropTypes.bool,
	};

	static defaultProps = {
		className: '',
		style: {},
		short: false,
		light: false,
		dateFormat: 'Y-m-d', // https://flatpickr.js.org/formatting/
		locale: 'en',
		datePickerType: 'single',
		disabled: false,
	};

	_carbonId1 = `carbon_date-picker-input-id__${uniqueId()}`;
	_carbonId2 = `carbon_date-picker-input-id__${uniqueId()}`;

	_getValidDate(date) {
		if (Moment.isMoment(date) && date.isValid()) {
			return date.toDate();
		}

		return date;
	}

	_onChange = dates => {
		const { onChange, datePickerType } = this.props;

		if (dates.length) {
			if (datePickerType === 'range') {
				const startDate = dates[0] && moment(dates[0]);
				const endDate = dates[1] && moment(dates[1]);
				onChange && onChange(startDate, endDate);
			} else {
				const date = dates[0] && moment(dates[0]);
				onChange && onChange(date);
			}
		}
	};

	render() {
		const { datePickerType, locale, dateFormat, disabled, className, ...others } = this.props;

		const commonPickeProps = {
			...others,
			locale,
			className,
			datePickerType,
			dateFormat,
		};

		if (datePickerType === 'range') {
			const {
				startDate,
				endDate,
				startDatePlaceholderText,
				endDatePlaceholderText,
				disabled,
			} = this.props;
			const pickeProps = {
				...commonPickeProps,
				value: [this._getValidDate(startDate), this._getValidDate(endDate)],
			};

			return (
				<CarbonDatePicker {...pickeProps} onChange={this._onChange}>
					<DatePickerInput
						id={this._carbonId1}
						placeholder={startDatePlaceholderText}
						disabled={disabled}
					/>
					<DatePickerInput
						id={this._carbonId2}
						placeholder={endDatePlaceholderText}
						disabled={disabled}
					/>
				</CarbonDatePicker>
			);
		} else {
			const { placeholder, date } = this.props;

			const pickeProps = {
				...commonPickeProps,
				value: this._getValidDate(date),
			};

			const inputProps = {
				id: this._carbonId1,
				placeholder,
				disabled,
			};

			return (
				<CarbonDatePicker {...pickeProps} onChange={this._onChange}>
					<DatePickerInput {...inputProps} />
				</CarbonDatePicker>
			);
		}
	}
}
